import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  VStack,
  Flex,
  Box,
  ModalCloseButton,
  Tooltip,
} from '@wegroup/design-system';
import { capitalize } from 'lodash';
import React from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { handleResponseLanguage } from '../../../../../../../../i18n';
import useMobileBreakpoints from '../../../../../../../common/src/hooks/useMobileBreakpoints';
import { isEmptyOrUndefined } from '../../../../../../../common/src/utils/objectUtils';
import { chakraCustomScrollBar } from '../../../../../../../common/src/utils/styledComponentsUtils';
import useFlowsHelper from '../../../../../hooks/useFlowsHelper';
import FlowsFormProvider from '../../../../../store/context/FlowsFormProvider';
import { Inputs } from '../../../../../types/Enums';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import { getFlowsDataTestId } from '../../../../../utils/testUtils';
import CompanyScanContentWrapper from '../../../../ui/company-scan/components/app-shell/CompanyScanContentWrapper';
import QuestionGroupHeader from '../../../../ui/company-scan/components/question-group/QuestionGroupHeader';
import QuestionGroupWrapper from '../../../../ui/company-scan/components/question-group/QuestionGroupWrapper';
import TableModalFormProvider from '../context/SubFormProvider';
import useAddTableItem from '../hooks/useAddTableItem';
import { useNextIndex } from '../hooks/useAvailableIndices';
import useDuplicateGroups from '../hooks/useDuplicateGroups';
import TableItemsModalToolbar from './TableItemsModalToolbar';

interface Props {
  question: FlowsQuestion;
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  currentIndex?: number;
}

const NewTableItemModal: React.FC<React.PropsWithChildren<Props>> = ({
  question,
  isOpen,
  onClose,
  onCancel,
  currentIndex,
}) => {
  const { t } = useTranslation();
  const { isLaptop } = useMobileBreakpoints();
  const { getAnswersForGroup } = useFlowsHelper();
  const groups = useDuplicateGroups(question, currentIndex);
  const addTableItem = useAddTableItem(question);
  const nextIndex = useNextIndex(question);

  const entity_name = handleResponseLanguage(
    question.rules.table?.entity_name,
  ).toLowerCase();
  const modalGoal = currentIndex !== -1 ? 'EDIT' : 'ADD';

  const addBuilding = () => {
    addTableItem(currentIndex);
    onClose();
  };

  const handleOnCancel = () => {
    if (modalGoal === 'ADD') onCancel();
    else onClose();
  };

  const isConfirmEnabled = groups.some(({ id }) => {
    return getAnswersForGroup(id, currentIndex).some(
      ({ answer }) => !isEmptyOrUndefined(answer),
    );
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => null}
      scrollBehavior="inside"
      size="5xl"
    >
      <ModalOverlay />
      <ModalContent>
        <TableModalFormProvider>
          <FlowsFormProvider
            key={currentIndex}
            currentIndex={currentIndex !== -1 ? currentIndex : nextIndex}
            groups={groups}
          >
            <ModalHeader>
              {capitalize(
                t(`FLOWS_INPUT_TABLE_${modalGoal}`, { entity: entity_name }),
              )}
            </ModalHeader>
            <ModalBody p="0" sx={chakraCustomScrollBar} as={Flex}>
              {/* Adding another form provider here reduces the render time */}

              {isLaptop && (
                <Box
                  display={{ base: 'none', tablet: 'block' }}
                  pos="sticky"
                  left="0"
                  top="0"
                >
                  <TableItemsModalToolbar
                    question={question}
                    currentGroups={groups}
                  />
                </Box>
              )}

              <CompanyScanContentWrapper>
                <VStack
                  spacing="8"
                  align="stretch"
                  maxW="4xl"
                  w="100%"
                  margin="0 auto"
                >
                  {groups.map((group) => {
                    const methods = useFormContext();

                    return (
                      <VStack
                        key={group.id}
                        align="stretch"
                        spacing="4"
                        /** negative margin to overwrite default indent of questiongroups */
                        ml="-10"
                      >
                        <QuestionGroupHeader pt="0" group={group} />
                        <QuestionGroupWrapper group={group} {...methods} />
                      </VStack>
                    );
                  })}
                </VStack>
              </CompanyScanContentWrapper>
            </ModalBody>
            <BuildingModalFooter
              currentIndex={currentIndex}
              onConfirm={addBuilding}
              onClose={handleOnCancel}
              onCancel={handleOnCancel}
              isConfirmEnabled={isConfirmEnabled}
              isDisabled={question.rules.is_disabled}
            />
          </FlowsFormProvider>
        </TableModalFormProvider>
      </ModalContent>
    </Modal>
  );
};

export default NewTableItemModal;

interface ModalFooterProps {
  currentIndex?: number;
  onClose: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  isConfirmEnabled: boolean;
  isDisabled?: boolean;
}

const BuildingModalFooter: React.FC<
  React.PropsWithChildren<ModalFooterProps>
> = ({
  currentIndex,
  onClose,
  onCancel,
  onConfirm,
  isConfirmEnabled,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { isDirty } = useFormState();
  const modalGoal = currentIndex !== -1 ? 'EDIT' : 'ADD';

  const closeFn = () => {
    if (modalGoal === 'EDIT') return handleConfirm();
    if (isDirty) {
      onCancel();
    } else {
      onClose();
    }
  };

  const { handleSubmit } = useFormContext();

  const handleConfirm = handleSubmit(() => {
    onConfirm();
  });

  return (
    <>
      <ModalCloseButton onClick={closeFn} />
      <ModalFooter>
        <Button variant="ghost" mr={3} onClick={closeFn}>
          {t('CLOSE')}
        </Button>
        {!isDisabled && (
          <Tooltip
            label={t('COMPANY-SCAN_INPUT_TABLE_ADD-DISABLED')}
            isDisabled={isConfirmEnabled}
            shouldWrapChildren
          >
            <Button
              onClick={handleConfirm}
              isDisabled={!isConfirmEnabled}
              data-test-id={getFlowsDataTestId(Inputs.TABLE, 'CONFIRM-ADD')}
            >
              {currentIndex !== -1 ? t('EDIT') : t('ADD')}
            </Button>
          </Tooltip>
        )}
      </ModalFooter>
    </>
  );
};
